import { isStandardBackendError } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { ERROR_CODES } from '@dealstack/shared/lib/common/errors';
import { BulkEntrySignStatus } from '@dealstack/shared/lib/common/eideasy/bulkSigning';
import { OutstandingReason } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import type { OutstandingDocument } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import type {
	BuildOutstandingDocumentsInput,
	ClassifyPackageCompletionInput,
	DrawPackageOutcome,
} from './useBulkDrawSign.types';

/** Whether one package's `bulk-complete` call left its document signed, and if not, why. */
export function classifyPackageCompletion(
	input: ClassifyPackageCompletionInput
): DrawPackageOutcome {
	const { instanceId } = input;

	if ('result' in input) {
		const entry = input.result.results.find((result) => result.instanceId === instanceId);
		if (!entry) {
			return { instanceId, signed: false, reason: OutstandingReason.UNCONFIRMED };
		}
		switch (entry.status) {
			case BulkEntrySignStatus.SIGNED:
			case BulkEntrySignStatus.ALREADY_SIGNED:
				return { instanceId, signed: true };
			case BulkEntrySignStatus.FAILED:
				return { instanceId, signed: false, reason: OutstandingReason.SAVE_FAILED };
			case BulkEntrySignStatus.NOT_RETURNED:
				return { instanceId, signed: false, reason: OutstandingReason.NOT_RETURNED };
			default:
				// Stops compiling when the backend adds a status; a stale bundle still lands here.
				entry.status satisfies never;
				return { instanceId, signed: false, reason: OutstandingReason.UNCONFIRMED };
		}
	}

	const { error } = input;
	if (
		isStandardBackendError(error) &&
		error.error.code === ERROR_CODES.GENERIC.CONTRACTS.SIGNING[3010105]
	) {
		return { instanceId, signed: false, reason: OutstandingReason.NOT_RETURNED };
	}
	// Includes a 404: the webhook may have completed the session, or it expired unsigned.
	return { instanceId, signed: false, reason: OutstandingReason.UNCONFIRMED };
}

/**
 * One entry per selected document left unsigned: packages first, in signing order, then the
 * documents held back at prepare.
 */
export function buildOutstandingDocuments({
	prepare,
	packageOutcomes,
}: BuildOutstandingDocumentsInput): OutstandingDocument[] {
	const reasonByInstanceId = new Map<string, OutstandingReason>();
	const recordReason = (instanceIds: string[] | undefined, reason: OutstandingReason) => {
		for (const instanceId of instanceIds ?? []) {
			if (!reasonByInstanceId.has(instanceId)) {
				reasonByInstanceId.set(instanceId, reason);
			}
		}
	};

	// First reason recorded wins, so a package failure outranks DEFERRED on the same document.
	for (const outcome of packageOutcomes) {
		if (outcome.signed) {
			continue;
		}
		recordReason([outcome.instanceId], outcome.reason);
	}
	recordReason(prepare.failedInstanceIds, OutstandingReason.PREPARE_FAILED);
	recordReason(prepare.unsignableInstanceIds, OutstandingReason.NOTHING_TO_SIGN);
	recordReason(prepare.skippedInstanceIds, OutstandingReason.ADVANCED_REQUIRED);
	recordReason(prepare.witnessSkippedInstanceIds, OutstandingReason.WITNESS_REQUIRED);
	recordReason(prepare.deferredInstanceIds, OutstandingReason.DEFERRED);

	const labelByInstanceId = new Map(
		(prepare.documentLabels ?? []).map((label) => [label.instanceId, label])
	);
	const orderedInstanceIds = new Set([
		...packageOutcomes.map((outcome) => outcome.instanceId),
		...reasonByInstanceId.keys(),
	]);

	return [...orderedInstanceIds].flatMap((instanceId) => {
		const reason = reasonByInstanceId.get(instanceId);
		if (!reason) {
			return [];
		}
		const label = labelByInstanceId.get(instanceId);
		return [
			{
				instanceId,
				templateName: label?.templateName ?? null,
				investorNames: label?.investorNames ?? [],
				reason,
			},
		];
	});
}
